import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { HelpCenterArticle, HelpCenterCatalogStatus } from '../../hooks/useHelpCenterCatalog';
import {
  HELP_CENTER_CATEGORIES,
  HELP_CENTER_CATEGORY_ICONS,
  type HelpCenterCategory,
} from '../../utils/helpCenterCategories';

type Props = {
  articles: HelpCenterArticle[];
  status: HelpCenterCatalogStatus;
  activeCategory?: HelpCenterCategory;
  activeSlug?: string;
};

const HelpCenterNav = ({ articles, status, activeCategory, activeSlug }: Props) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(activeCategory ? [activeCategory] : [])
  );

  useEffect(() => {
    if (!activeCategory) {
      return;
    }
    setExpanded((current) => {
      if (current.has(activeCategory)) {
        return current;
      }
      const next = new Set(current);
      next.add(activeCategory);
      return next;
    });
  }, [activeCategory]);

  if (status === 'loading') {
    return <p className="text-slate-700">{t('helpCenter.loading')}</p>;
  }

  if (status === 'error') {
    return <p className="text-slate-700">{t('helpCenter.load_error')}</p>;
  }

  const toggleCategory = (category: HelpCenterCategory) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  return (
    <nav aria-label={`${t('helpCenter.nav_label')}`} className="text-left">
      <ul className="space-y-1">
        {HELP_CENTER_CATEGORIES.map((category) => {
          const categoryArticles = articles.filter((article) => article.category === category);
          const isExpanded = expanded.has(category);
          const Icon = HELP_CENTER_CATEGORY_ICONS[category];
          const isActiveCategory = activeCategory === category;

          return (
            <li key={category}>
              <div className="flex items-center gap-1">
                {categoryArticles.length > 0 ? (
                  <button
                    type="button"
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    aria-expanded={isExpanded}
                    aria-label={
                      isExpanded
                        ? `${t('helpCenter.collapse_articles')}`
                        : `${t('helpCenter.expand_articles')}`
                    }
                    onClick={() => toggleCategory(category)}
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" strokeWidth={1.75} />
                    ) : (
                      <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
                    )}
                  </button>
                ) : (
                  <span className="inline-block h-7 w-7 shrink-0" aria-hidden="true" />
                )}
                <Link
                  to={`/help-center/category/${category}`}
                  className={`flex min-w-0 flex-1 items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-slate-100 ${
                    isActiveCategory && !activeSlug
                      ? 'font-semibold text-slate-900'
                      : 'font-medium text-slate-800'
                  }`}
                >
                  <Icon className="h-4 w-4 shrink-0 text-slate-700" strokeWidth={1.75} />
                  <span className="truncate">{t(`helpCenter.category.${category}`)}</span>
                </Link>
              </div>
              {isExpanded && categoryArticles.length > 0 ? (
                <ul className="mb-2 ml-9 mt-1 space-y-1">
                  {categoryArticles.map((article) => {
                    const isActiveArticle = article.slug === activeSlug;
                    return (
                      <li key={article.slug}>
                        <Link
                          to={`/help-center/${article.slug}`}
                          className={`block rounded px-1.5 py-1 text-sm hover:bg-slate-100 ${
                            isActiveArticle
                              ? 'font-semibold text-slate-900'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                        >
                          {article.title}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default HelpCenterNav;
